// Global Instructions Rule Applied!
//
// Proposal payload → review form, and back.
//
// Four of the five write tools need nothing here: their payload IS the record
// the matching DrawerNew seeds from and emits (mergeDealIntoInitialFormData
// learned the canonical keys, and the other three drawers merge a record
// directly), because every apply() calls the same controller the human New form
// posts to. This module exists for the OUTLIER — log_interaction, whose form
// speaks loose value names (`subject`, `type`, `owner`…) rather than the
// prefixed record keys — plus the small bits of routing the review host needs.

/** The five write tools, and which surface reviews each. */
export type ProposalEntity = 'deal' | 'company' | 'contact' | 'prospect' | 'interaction'

export const TOOL_TO_ENTITY: Record<string, ProposalEntity> = {
  create_deal: 'deal',
  create_company: 'company',
  create_contact: 'contact',
  create_prospect: 'prospect',
  log_interaction: 'interaction'
}

/** Title shown on the review surface, per entity. */
export const ENTITY_REVIEW_TITLE: Record<ProposalEntity, string> = {
  deal: 'Review proposal — New Deal',
  company: 'Review proposal — New Company',
  contact: 'Review proposal — New Contact',
  prospect: 'Review proposal — New Prospect',
  interaction: 'Review proposal — Log interaction'
}

/**
 * The record key holding the PRIMARY id an applied proposal created, per
 * entity, and the tab module that opens it.
 *
 * Primary only, deliberately: a create_prospect result also carries
 * `accounts__id`, which is the joined investor account whether or not this
 * write created it. Opening that could land the user on a company the agent
 * never touched.
 */
export const ENTITY_OPEN_TARGET: Record<
  ProposalEntity,
  { resultKey: string; module: string } | null
> = {
  deal: { resultKey: 'deals__id', module: 'deals' },
  company: { resultKey: 'accounts__id', module: 'companies' },
  contact: { resultKey: 'contacts__id', module: 'contacts' },
  prospect: { resultKey: 'prospects__id', module: 'fundraising' },
  // Interactions have no full-form route — they render inside a parent
  // record's timeline — so an applied interaction has nothing of its own to
  // open. Handled as "no Open button" rather than a link that goes nowhere.
  interaction: null
}

const INTERACTIONS = 'interactions__'
const PROG_PARTICIPANTS = 'prog__interaction_participants'

/** Record → the loose values InteractionForm takes. */
export function interactionRecordToFormValues(
  record: Record<string, any> | null | undefined
): Record<string, any> {
  const r = record ?? {}
  const datetime = r[`${INTERACTIONS}datetime`] ?? undefined
  return {
    subject: r[`${INTERACTIONS}subject`] ?? '',
    type: r[`${INTERACTIONS}type`] ?? '',
    description: r[`${INTERACTIONS}description`] ?? '',
    notes: r[`${INTERACTIONS}notes`] ?? '',
    owner: r[`${INTERACTIONS}owner`] ?? null,
    participants: r[PROG_PARTICIPANTS] ?? [],
    ...(datetime ? { datetime } : {})
  }
}

/**
 * What the form produced, merged BACK over the proposal payload.
 *
 * THE FORM IS ASYMMETRIC, and this is the trap: `initialValues` takes the LOOSE
 * names (`subject`, `type`, `owner`…) but `getValues()` returns the prefixed
 * RECORD (`interactions__subject`, …). So the inbound adapter maps names and
 * this one does not — treating getValues() as loose silently dropped every edit,
 * because none of the loose keys were present to copy.
 *
 * Spreading over the original preserves what the form has no fields for: the
 * parent links (`interaction_accounts__account_id` and friends), which the
 * review surface shows read-only because changing them would make this a
 * different proposal.
 *
 * The id is pinned back to the proposal's. `getValues()` mints a fresh
 * `crypto.randomUUID()` every call — correct when it is creating an interaction
 * from scratch, wrong here, where `_build_interaction` already assigned one and
 * the proposal's own result must point at it.
 */
export function interactionFormValuesToRecord(
  original: Record<string, any> | null | undefined,
  formRecord: Record<string, any>
): Record<string, any> {
  const filed = original ?? {}
  const merged: Record<string, any> = { ...filed, ...formRecord }
  const filedId = filed[`${INTERACTIONS}id`]
  if (filedId) merged[`${INTERACTIONS}id`] = filedId
  return merged
}

/**
 * Which parent an interaction proposal attaches to — shown read-only on the
 * review surface. log_interaction refuses to build without one of these, so
 * there is always exactly one to name.
 */
export function interactionParentRef(
  record: Record<string, any> | null | undefined
): { module: string; id: string } | null {
  const r = record ?? {}
  const account = r.interaction_accounts__account_id
  if (account) return { module: 'companies', id: String(account) }
  const deal = r.interaction_deals__deal_id
  if (deal) return { module: 'deals', id: String(deal) }
  const prospect = r.interaction_prospects__prospect_id
  if (prospect) return { module: 'fundraising', id: String(prospect) }
  return null
}

/** Value-equality, so an untouched form does not record an amendment. */
export function payloadsEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

/**
 * The company-reference field per entity, and why it needs hydrating.
 *
 * The New drawers hold a company reference as the PAIR `{value, label}` — the
 * value is what `extractCompanyId` unwraps at save, the label is what the
 * Select displays. A proposal payload carries the bare id, because that is what
 * `process_new_*` consumes.
 *
 * Seeded raw, the reviewer would therefore see a UUID where the company name
 * belongs — on the one screen whose entire purpose is letting them see what
 * they are approving. So the review host resolves the name first.
 */
export const ENTITY_COMPANY_REF_FIELD: Partial<Record<ProposalEntity, string>> = {
  deal: 'deals__account',
  prospect: 'prospects__investor'
}

/**
 * Replace a bare-id company reference with the `{value, label}` pair the form
 * displays. `resolveName` returns null for an id it cannot resolve, in which
 * case the field is left exactly as it was — a raw id is poor, but dropping the
 * reference would silently change what the proposal does.
 */
export async function hydrateCompanyRef(
  entity: ProposalEntity,
  payload: Record<string, any>,
  resolveName: (id: string) => Promise<string | null>
): Promise<Record<string, any>> {
  const field = ENTITY_COMPANY_REF_FIELD[entity]
  if (!field) return payload
  const value = payload[field]
  if (typeof value !== 'string' || !value.trim()) return payload

  const label = await resolveName(value)
  if (!label) return payload
  return { ...payload, [field]: { value, label } }
}
